import { keyBy } from 'lodash';
import memoizeOne from 'memoize-one';
import { useQuery } from '@tanstack/react-query';
import { getCollections } from '../services/getCollections';
import { useCollectionsContext } from '../context/CollectionsContext';
import { isSignUpOrMigrationRequired } from '../utils/isSignUpOrMigrationRequired';
import { RawCollection } from '../types';
import { useRequireCollectionsProvider } from './useRequireCollectionsProvider';

const NUM_RETRIES = 5;
const STALE_TIME = 30 * 1000;
const INITIAL_DATA_UPDATED_AT = Date.now();

export const resourceTypeToLookupKey = {
    GIG: 'gigs',
    SELLER: 'sellers',
    INSPIRE_DELIVERY: 'inspireDeliveries',
} as const;

export interface CollectionResourceEdge {
    itemId: string;
    collectionName: string;
    pendingRemoval?: boolean;
}

export interface CollectionResourceEdgeMap {
    [collectionId: string]: CollectionResourceEdge;
}

interface CollectionsLookup {
    gigs: { [resourceId: string]: CollectionResourceEdgeMap };
    sellers: { [resourceId: string]: CollectionResourceEdgeMap };
    inspireDeliveries: { [resourceId: string]: CollectionResourceEdgeMap };
}

interface UserCollectionsProps {
    enabled?: boolean;
    initialCollections?: RawCollection[];
}

const buildCollectionsLookup = (data?: RawCollection[]): CollectionsLookup => {
    const lookup: CollectionsLookup = {
        gigs: {},
        sellers: {},
        inspireDeliveries: {},
    };

    if (!data) {
        return lookup;
    }

    data.forEach((collection) => {
        collection.items.forEach((item) => {
            const lookupKey: keyof CollectionsLookup = resourceTypeToLookupKey[item.resourceType];

            if (!lookupKey) {
                return;
            }

            if (!lookup[lookupKey][item.resourceId]) {
                lookup[lookupKey][item.resourceId] = {};
            }

            lookup[lookupKey][item.resourceId][collection.id] = {
                itemId: item.id,
                collectionName: collection.name,
                pendingRemoval: item.pendingRemoval,
            };
        });
    });

    return lookup;
};

/**
 * Selector function to get the derived state from the raw collections data.
 * This is memoized to avoid unnecessary re-computations, such as multiple cards on a page.
 */
const select = memoizeOne((data: RawCollection[] | undefined) => ({
    collections: data,
    collectionsLookup: buildCollectionsLookup(data),
    collectionsById: keyBy(data, 'id'),

    // memoizeOne checks for changes to `this`, so to say we don't care
    // about changes to `this` we bind it to null.
})).bind(null);

export const useCollections = ({
    enabled: propEnabled = true,
    initialCollections: initialCollectionsProps,
}: UserCollectionsProps = {}) => {
    useRequireCollectionsProvider('useCollections');
    const { initialCollections: initialCollectionsContext } = useCollectionsContext();
    const initialCollections = initialCollectionsProps || initialCollectionsContext;
    const canGetCollections = !isSignUpOrMigrationRequired();
    const enabled = propEnabled && canGetCollections;
    const { data, isLoading } = useQuery({
        enabled,
        queryKey: ['collections'],
        queryFn: () => getCollections(),
        refetchOnWindowFocus: false,
        retry: NUM_RETRIES,
        staleTime: STALE_TIME,
        initialData: initialCollections,
        initialDataUpdatedAt: INITIAL_DATA_UPDATED_AT,
        select,
    });

    const { collections, collectionsLookup, collectionsById } = data || select(initialCollections);

    return {
        collections,
        collectionsById,
        collectionsLookup,
        isLoading: isLoading && enabled,
    };
};
